import React, { useState, useEffect } from "react";
import { X, Calculator, AlertTriangle } from "lucide-react";
import {
  calcolaRavvedimento,
  formatEuro,
  formatPercentuale,
} from "../../utils/ravvedimentoCalc";

function todayISO() {
  return new Date().toISOString().split("T")[0];
}

export default function RavvedimentoCalculator({
  isOpen,
  onClose,
  darkMode = false,
}) {
  const [importo, setImporto] = useState("");
  const [dataScadenza, setDataScadenza] = useState("");
  const [dataPagamento, setDataPagamento] = useState(todayISO());
  const [risultato, setRisultato] = useState(null);
  const [errors, setErrors] = useState({});

  // Reset quando si apre il modal
  useEffect(() => {
    if (isOpen) {
      setImporto("");
      setDataScadenza("");
      setDataPagamento(todayISO());
      setRisultato(null);
      setErrors({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const parseAmount = (str) => {
    if (!str) return 0;
    const cleaned = str.replace(/\s/g, "").replace(",", ".");
    return parseFloat(cleaned) || 0;
  };

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9,.]/g, "");
    setAmount(value);
  };

  const setAmount = (value) => {
    setImporto(value);
    setRisultato(null); // Reset risultato quando cambiano i dati
  };

  const validate = () => {
    const newErrors = {};

    const numImporto = parseAmount(importo);
    if (!importo || numImporto <= 0) {
      newErrors.importo = "Inserisci un importo valido maggiore di 0";
    }

    if (!dataScadenza) {
      newErrors.dataScadenza = "La data scadenza è obbligatoria";
    }

    if (!dataPagamento) {
      newErrors.dataPagamento = "La data pagamento è obbligatoria";
    } else if (dataPagamento < dataScadenza) {
      newErrors.dataPagamento = "La data pagamento non può essere anteriore alla scadenza";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCalculate = (e) => {
    e.preventDefault();

    if (!validate()) return;

    const result = calcolaRavvedimento(
      parseAmount(importo),
      dataScadenza,
      dataPagamento
    );

    setRisultato(result);
  };

  const handleReset = () => {
    setImporto("");
    setDataScadenza("");
    setDataPagamento(todayISO());
    setRisultato(null);
    setErrors({});
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className={`relative w-full max-w-lg rounded-2xl shadow-xl ${
            darkMode ? "bg-neutral-900" : "bg-white"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className={`flex items-center justify-between px-6 py-4 border-b ${
              darkMode ? "border-neutral-800" : "border-neutral-200"
            }`}
          >
            <div className="flex items-center gap-2">
              <Calculator
                className={`w-5 h-5 ${
                  darkMode ? "text-forfy-300" : "text-forfy-600"
                }`}
              />
              <h2
                className={`text-lg font-semibold ${
                  darkMode ? "text-white" : "text-neutral-900"
                }`}
              >
                Calcola Ravvedimento Operoso
              </h2>
            </div>
            <button
              onClick={onClose}
              className={`p-1.5 rounded-lg transition-colors ${
                darkMode
                  ? "hover:bg-neutral-800 text-neutral-500"
                  : "hover:bg-neutral-100 text-neutral-500"
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-5">
            {/* Form inputs */}
            <form onSubmit={handleCalculate} className="space-y-4">
              {/* Importo */}
              <div>
                <label
                  htmlFor="ravvImporto"
                  className={`block text-sm font-medium mb-1.5 ${
                    darkMode ? "text-neutral-400" : "text-neutral-700"
                  }`}
                >
                  Importo dovuto <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span
                    className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${
                      darkMode ? "text-neutral-500" : "text-neutral-500"
                    }`}
                  >
                    €
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    id="ravvImporto"
                    value={importo}
                    onChange={handleAmountChange}
                    placeholder="0,00"
                    className={`w-full pl-8 pr-3 py-2.5 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forfy-500 focus:border-forfy-500 ${
                      errors.importo
                        ? darkMode
                          ? "border-red-700 bg-red-900/30"
                          : "border-red-300 bg-red-50"
                        : darkMode
                        ? "border-neutral-700 bg-neutral-800 text-white"
                        : "border-neutral-300 bg-white text-neutral-900"
                    }`}
                  />
                </div>
                {errors.importo && (
                  <p className="mt-1 text-sm text-red-500">{errors.importo}</p>
                )}
              </div>

              {/* Date row */}
              <div className="grid grid-cols-2 gap-4">
                {/* Data Scadenza */}
                <div>
                  <label
                    htmlFor="ravvDataScadenza"
                    className={`block text-sm font-medium mb-1.5 ${
                      darkMode ? "text-neutral-400" : "text-neutral-700"
                    }`}
                  >
                    Data scadenza <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    id="ravvDataScadenza"
                    value={dataScadenza}
                    onChange={(e) => {
                      setDataScadenza(e.target.value);
                      setRisultato(null);
                    }}
                    className={`w-full px-3 py-2.5 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forfy-500 focus:border-forfy-500 ${
                      errors.dataScadenza
                        ? darkMode
                          ? "border-red-700 bg-red-900/30"
                          : "border-red-300 bg-red-50"
                        : darkMode
                        ? "border-neutral-700 bg-neutral-800 text-white"
                        : "border-neutral-300 bg-white text-neutral-900"
                    }`}
                  />
                  {errors.dataScadenza && (
                    <p className="mt-1 text-sm text-red-500">{errors.dataScadenza}</p>
                  )}
                </div>

                {/* Data Pagamento */}
                <div>
                  <label
                    htmlFor="ravvDataPagamento"
                    className={`block text-sm font-medium mb-1.5 ${
                      darkMode ? "text-neutral-400" : "text-neutral-700"
                    }`}
                  >
                    Data pagamento <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    id="ravvDataPagamento"
                    value={dataPagamento}
                    onChange={(e) => {
                      setDataPagamento(e.target.value);
                      setRisultato(null);
                    }}
                    className={`w-full px-3 py-2.5 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forfy-500 focus:border-forfy-500 ${
                      errors.dataPagamento
                        ? darkMode
                          ? "border-red-700 bg-red-900/30"
                          : "border-red-300 bg-red-50"
                        : darkMode
                        ? "border-neutral-700 bg-neutral-800 text-white"
                        : "border-neutral-300 bg-white text-neutral-900"
                    }`}
                  />
                  {errors.dataPagamento && (
                    <p className="mt-1 text-sm text-red-500">{errors.dataPagamento}</p>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleReset}
                  className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                    darkMode
                      ? "bg-neutral-800 text-neutral-400 hover:bg-neutral-700"
                      : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                  }`}
                >
                  Reset
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 rounded-lg font-medium text-sm bg-forfy-500 text-white hover:bg-forfy-600 transition-colors"
                >
                  Calcola
                </button>
              </div>
            </form>

            {/* Risultato */}
            {risultato && (
              <div
                className={`rounded-xl border-2 p-4 space-y-3 ${
                  darkMode
                    ? "border-neutral-700 bg-neutral-800/50"
                    : "border-neutral-200 bg-neutral-50"
                }`}
              >
                <h3
                  className={`font-semibold ${
                    darkMode ? "text-white" : "text-neutral-900"
                  }`}
                >
                  Risultato
                </h3>

                {risultato.isPuntuale ? (
                  <p
                    className={`text-sm ${
                      darkMode ? "text-green-400" : "text-green-600"
                    }`}
                  >
                    Pagamento puntuale, nessun ravvedimento necessario.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {/* Riepilogo */}
                    <div
                      className={`text-sm space-y-1 ${
                        darkMode ? "text-neutral-300" : "text-neutral-700"
                      }`}
                    >
                      <div className="flex justify-between">
                        <span>Giorni di ritardo:</span>
                        <span className="font-medium">{risultato.giorniRitardo}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Fascia:</span>
                        <span className="font-medium text-right max-w-[60%]">
                          {risultato.fascia}
                        </span>
                      </div>
                    </div>

                    {/* Dettaglio importi */}
                    <div
                      className={`border-t pt-2 space-y-1 text-sm ${
                        darkMode ? "border-neutral-700" : "border-neutral-200"
                      }`}
                    >
                      <div
                        className={`flex justify-between ${
                          darkMode ? "text-neutral-300" : "text-neutral-700"
                        }`}
                      >
                        <span>Importo originale:</span>
                        <span>{formatEuro(risultato.importoOriginale)}</span>
                      </div>
                      <div
                        className={`flex justify-between ${
                          darkMode ? "text-neutral-300" : "text-neutral-700"
                        }`}
                      >
                        <span>
                          Sanzione ({formatPercentuale(risultato.percentualeSanzione)}):
                        </span>
                        <span>{formatEuro(risultato.sanzione)}</span>
                      </div>
                      <div
                        className={`flex justify-between ${
                          darkMode ? "text-neutral-300" : "text-neutral-700"
                        }`}
                      >
                        <span>Interessi legali:</span>
                        <span>{formatEuro(risultato.interessi)}</span>
                      </div>
                    </div>

                    {/* Totale */}
                    <div
                      className={`border-t pt-2 flex justify-between font-semibold ${
                        darkMode
                          ? "border-neutral-700 text-white"
                          : "border-neutral-200 text-neutral-900"
                      }`}
                    >
                      <span>Totale da versare:</span>
                      <span className="text-forfy-500">{formatEuro(risultato.totale)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Disclaimer */}
            <div
              className={`flex gap-3 p-3 rounded-lg ${
                darkMode ? "bg-amber-900/20" : "bg-amber-50"
              }`}
            >
              <AlertTriangle
                className={`w-5 h-5 flex-shrink-0 ${
                  darkMode ? "text-amber-400" : "text-amber-600"
                }`}
              />
              <p
                className={`text-xs ${
                  darkMode ? "text-amber-300" : "text-amber-800"
                }`}
              >
                Calcolo indicativo. Per versamenti con ravvedimento, verificare
                sempre gli importi con un commercialista o con l'Agenzia delle
                Entrate.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div
            className={`px-6 py-4 border-t ${
              darkMode ? "border-neutral-800" : "border-neutral-200"
            }`}
          >
            <button
              onClick={onClose}
              className={`w-full px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                darkMode
                  ? "bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                  : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
              }`}
            >
              Chiudi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
